import { action, runInAction, observable } from 'mobx';
import cloneDeep from 'lodash/cloneDeep';
import { ErrorInterface } from 'modules/core/api/globalApi';
import schedulerController from './controller';
import {
  PostInterface,
  CreatePostInterface,
  UpdatePostInterface,
  DeleteAndRestorePostInterface,
  GetPostRequest,
  ServicePostInterface,
  GetServicePostRequest,
  CreateServicePostInterface,
  DeleteAndRestoreServicePosInterface,
  PostOfficeWeekInterface,
  CreatePostOfficeWeekInterface,
  UpdatePostOfficeWeekInterface,
} from '../api/schedulerReferenceApi';
import { getTimeInMinutes } from './schedulerFunctions';

export interface SchedulerReferenceStore {
  posts: PostInterface[],
  getPostRequest: GetPostRequest,
  currentPost: PostInterface,

  servicePosts: ServicePostInterface[],
  getServicePostRequest: GetServicePostRequest,
  currentServicePost: ServicePostInterface,
  // loading: boolean,

  postsForSelector: PostInterface[],
  getPostForSelectorByRequest: GetPostRequest,

  servicePostsForSelector: ServicePostInterface[],
  getServicePostForSelectorByRequest: GetServicePostRequest,

  postOfficeWeek: PostOfficeWeekInterface,
  postOfficeWeekByPostOfficeId: string,

  error: ErrorInterface,
}

const initialValues: SchedulerReferenceStore = {
  posts: [],
  getPostRequest: null,
  currentPost: null,

  servicePosts: [],
  getServicePostRequest: null,
  currentServicePost: null,
  // loading: false,

  postsForSelector: [],
  getPostForSelectorByRequest: null,

  servicePostsForSelector: [],
  getServicePostForSelectorByRequest: null,

  postOfficeWeek: null,
  postOfficeWeekByPostOfficeId: null,

  error: null,
};

export const schedulerReferenceStore = observable(initialValues);

export const schedulerReferenceActions = {

  async getPostOffices(request :GetPostRequest) {
    schedulerReferenceActions.clearError();
    runInAction(() => {
      schedulerReferenceStore.getPostRequest = request;
    });
    const response = await schedulerController.getPostOffices(request);
    schedulerReferenceActions.handleError(response);
    runInAction(() => {
      schedulerReferenceStore.posts = response.data;
    //  schedulerReferenceStore.loading = false;
    });
  },

  createPost: async (request: CreatePostInterface): Promise<void> => {
    schedulerReferenceActions.clearError();
    const response = await schedulerController.createPost(request);
    schedulerReferenceActions.handleError(response);
    runInAction(() => {
      if (!response.errorData) {
        schedulerReferenceStore.posts.push(response.data);
      }
    });
  },

  updatePost: async (request: UpdatePostInterface): Promise<void> => {
    schedulerReferenceActions.clearError();
    const response = await schedulerController.updatePost(request);
    schedulerReferenceActions.handleError(response);
    runInAction(() => {
      if (!response.errorData) {
        schedulerReferenceStore.posts = schedulerReferenceStore.posts
          .map((v) => (v.id === response.data.id
            ? { ...response.data }
            : { ...v }));
      }
    });
  },

  deletePost: async (request: DeleteAndRestorePostInterface): Promise<void> => {
    schedulerReferenceActions.clearError();
    const response = await schedulerController.deletePost(request);
    schedulerReferenceActions.handleError(response);
    runInAction(() => {
      if (!response.errorData) {
        schedulerReferenceStore.posts
          .find((v) => v.id === request.id).postDeletedDate = 'deleted';
      }
    });
  },

  restorePost: async (request: DeleteAndRestorePostInterface): Promise<void> => {
    schedulerReferenceActions.clearError();
    const response = await schedulerController.restorePost(request);
    schedulerReferenceActions.handleError(response);
    runInAction(() => {
      if (!response.errorData) {
        schedulerReferenceStore.posts = schedulerReferenceStore.posts
          .map((v) => (v.id === request.id
            ? { ...v, postDeletedDate: null }
            : { ...v }));
      }
    });
  },

  handleError: action((response, id:string = null) => {
    if (response.errorData) {
      const { errorData } = response;
      schedulerReferenceStore.error = {
        message: errorData?.message,
        errorCode: errorData?.errorCode,
        timestamp: errorData?.timestamp,
        status: response?.status,
        connectError: response?.connectError,
        isError: response?.error,
        id,
      };
      // schedulerReferenceStore.loading = false;
      throw errorData;
    }
  }),

  handleErrorWithoutThrow: action((response) => {
    if (response.errorData) {
      const { errorData } = response;
      // schedulerReferenceStore.loading = false;
      schedulerReferenceStore.error = errorData;
    }
  }),

  async clearPosts() {
    try {
      runInAction(() => {
        schedulerReferenceStore.posts = [];
        schedulerReferenceStore.getPostRequest = null;
      });
    } catch (err) {
      this.handleError(err);
    }
  },

  setCurrentPost: async (data: PostInterface): Promise<void> => {
    schedulerReferenceActions.clearError();
    runInAction(() => {
      schedulerReferenceStore.currentPost = data;
    });
  },

  clearCurrentPost: async (): Promise<void> => {
    schedulerReferenceActions.clearError();
    runInAction(() => {
      schedulerReferenceStore.currentPost = null;
      schedulerReferenceStore.servicePosts = [];
      schedulerReferenceStore.getServicePostRequest = null;
      schedulerReferenceStore.postOfficeWeek = null;
      schedulerReferenceStore.postOfficeWeekByPostOfficeId = null;
    });
  },

  clearError: action(() => {
    schedulerReferenceStore.error = null;
  }),

  async getServicePosts(request :GetServicePostRequest) {
    schedulerReferenceActions.clearError();
    schedulerReferenceStore.getServicePostRequest = request;
    const response = await schedulerController.getServicePosts(request);
    schedulerReferenceActions.handleError(response);
    runInAction(() => {
      schedulerReferenceStore.servicePosts = response.data;
    //  schedulerReferenceStore.loading = false;
    });
  },

  createServicePost: async (request: CreateServicePostInterface): Promise<void> => {
    schedulerReferenceActions.clearError();
    const response = await schedulerController.createServicePost(request);
    schedulerReferenceActions.handleError(response);
    /* runInAction(() => {
      if (!response.errorData) {
        schedulerReferenceStore.servicePosts.push(response.data);
      }
    }); */
  },

  deleteServicePost: async (request: DeleteAndRestoreServicePosInterface): Promise<void> => {
    schedulerReferenceActions.clearError();
    const response = await schedulerController.deleteServicePost(request);
    schedulerReferenceActions.handleError(response);
    runInAction(() => {
      if (!response.errorData) {
        schedulerReferenceStore.servicePosts
          .find((v) => v.id === request.id).deletedDate = 'deleted';
      }
    });
  },

  restoreServicePost: async (request: DeleteAndRestoreServicePosInterface): Promise<void> => {
    schedulerReferenceActions.clearError();
    const response = await schedulerController.restoreServicePost(request);
    schedulerReferenceActions.handleError(response);
    runInAction(() => {
      if (!response.errorData) {
        schedulerReferenceStore.servicePosts = schedulerReferenceStore.servicePosts
          .map((v) => (v.id === request.id
            ? { ...v, deletedDate: null }
            : { ...v }));
      }
    });
  },

  async clearServicePosts() {
    try {
      runInAction(() => {
        schedulerReferenceStore.servicePosts = [];
        schedulerReferenceStore.getServicePostRequest = null;
      });
    } catch (err) {
      this.handleError(err);
    }
  },

  setCurrentServicePost: async (data: ServicePostInterface): Promise<void> => {
    schedulerReferenceActions.clearError();
    runInAction(() => {
      schedulerReferenceStore.currentServicePost = data;
    });
  },

  clearCurrentServicePost: async (): Promise<void> => {
    schedulerReferenceActions.clearError();
    runInAction(() => {
      schedulerReferenceStore.currentServicePost = null;
    });
  },

  async getPostsForSelector(request :GetPostRequest) {
    schedulerReferenceActions.clearError();
    // reconciliationActions.setLoading();
    runInAction(() => {
      schedulerReferenceStore.getPostForSelectorByRequest = request;
    });
    const response = await schedulerController.getPostOffices(request);
    schedulerReferenceActions.handleError(response);
    runInAction(() => {
      schedulerReferenceStore.postsForSelector = response.data;
    //  schedulerReferenceStore.loading = false;
    });
  },

  async clearPostsForSelector() {
    try {
      runInAction(() => {
        schedulerReferenceStore.postsForSelector = [];
        schedulerReferenceStore.getPostForSelectorByRequest = null;
      });
    } catch (err) {
      this.handleError(err);
    }
  },

  async getServicePostsForSelector(request :GetServicePostRequest) {
    schedulerReferenceActions.clearError();
    schedulerReferenceStore.getServicePostForSelectorByRequest = request;
    const response = await schedulerController.getServicePosts(request);
    schedulerReferenceActions.handleError(response);
    runInAction(() => {
      schedulerReferenceStore.servicePostsForSelector = response.data;
    //  schedulerReferenceStore.loading = false;
    });
  },

  async clearServicePostsForSelector() {
    try {
      runInAction(() => {
        schedulerReferenceStore.servicePostsForSelector = [];
        schedulerReferenceStore.getServicePostForSelectorByRequest = null;
      });
    } catch (err) {
      this.handleError(err);
    }
  },

  async getPostOfficeWeek(postOfficeId) {
    schedulerReferenceActions.clearError();
    runInAction(() => {
      schedulerReferenceStore.postOfficeWeekByPostOfficeId = postOfficeId;
    });
    const response = await schedulerController.getPostOfficeWeek(postOfficeId);
    if (response.errorData) {
      schedulerReferenceStore.postOfficeWeekByPostOfficeId = null;
      schedulerReferenceActions.handleError(response);
    } else {
       
      for (const itemDay of response.data.days) {
        itemDay.times.sort((
          a: { beginTime: string; },
          b: { endTime: string; }
        ) => getTimeInMinutes(a.beginTime) - getTimeInMinutes(b.endTime));
      }
      runInAction(() => {
        schedulerReferenceStore.postOfficeWeek = response.data;
      });
    }
  },

  genId() { return `f${ (+new Date()).toString(16) }`; },

  async addEmptyTimes(dayOfWeek: number) {
    const x = cloneDeep(schedulerReferenceStore.postOfficeWeek);
    x.days[dayOfWeek - 1].times.push(
      {
        beginTime: '',
        beginTimeIsError: true,
        endTime: '',
        endTimeIsError: true,
        id: schedulerReferenceActions.genId(),
        created: true,
      }
    );
    runInAction(() => {
      schedulerReferenceStore.postOfficeWeek = cloneDeep(x);
    });
  },

  async setBeginTime(dayOfWeek: number, id: string, time: string, isError: boolean) {
    const x = cloneDeep(schedulerReferenceStore.postOfficeWeek);
    const y = x.days[dayOfWeek - 1].times.find((v) => v.id === id);
    y.beginTime = time;
    y.beginTimeIsError = isError;
    if (!y.created) { y.updated = true; }
    runInAction(() => {
      schedulerReferenceStore.postOfficeWeek = cloneDeep(x);
    });
  },

  async setEndTime(dayOfWeek: number, id: string, time: string, isError: boolean) {
    const x = cloneDeep(schedulerReferenceStore.postOfficeWeek);
    const y = x.days[dayOfWeek - 1].times.find((v) => v.id === id);
    y.endTime = time;
    y.endTimeIsError = isError;
    if (!y.created) { y.updated = true; }
    runInAction(() => {
      schedulerReferenceStore.postOfficeWeek = cloneDeep(x);
    });
  },

  async deleteTimes(dayOfWeek:number, id: string) {
    const x = cloneDeep(schedulerReferenceStore.postOfficeWeek);
    x.days[dayOfWeek - 1].times.find((v) => v.id === id).deleted = true;
    runInAction(() => {
      schedulerReferenceStore.postOfficeWeek = cloneDeep(x);
    });
  },

  createPostOfficeWeeks: async (officePostId:string): Promise<void> => {
    // Тут сделана последовательная запись изменений, т.к. бэк проверяет валидность интервалов ( не пересечение интервалов )
    // promise.all может привести к ошибкам
     
    for (const itemDay of schedulerReferenceStore.postOfficeWeek.days) {
       
      for (const itemTime of itemDay.times) {
        if (itemTime.created && !itemTime.deleted) {
           
          await schedulerReferenceActions.createPostOfficeWeek(itemTime.id, {
            beginTimeHour: itemTime.beginTime.split(':')[0],
            beginTimeMin: itemTime.beginTime.split(':')[1],
            dayOfWeek: itemDay.dayOfWeek.toString(),
            endTimeHour: itemTime.endTime.split(':')[0],
            endTimeMin: itemTime.endTime.split(':')[1],
            officePostId,
          });
        }
        if (itemTime.updated && !itemTime.deleted) {
           
          await schedulerReferenceActions.updatePostOfficeWeek(
            itemTime.id,
            itemDay.dayOfWeek,
            {
              beginTimeHour: itemTime.beginTime.split(':')[0],
              beginTimeMin: itemTime.beginTime.split(':')[1],
              endTimeHour: itemTime.endTime.split(':')[0],
              endTimeMin: itemTime.endTime.split(':')[1],
            }
          );
        }
        if (itemTime.deleted && !itemTime.created) {
           
          await schedulerReferenceActions.deletePostOfficeWeek(itemTime.id, itemDay.dayOfWeek);
        }
      }
    }
  },

  updateIdInPostOfficeWeeks: async (dayOfWeek:number, oldId:string, newId?:string): Promise<void> => {
    // функция заменять id в недельном расписании
    // + убирает пометки created, updated, deleted чтоб пользователь мог продолжить редактировать расписание
    // из-за особенностей интерфейса пришлось докрутить - пользователь может редактировать и нажимать кнопку "Сохранить" несколько раз
    // перезагрузку с бэка полностью не совсем корректно для интерфеса (моргание + сортировки могут скакать)
    const z = cloneDeep(schedulerReferenceStore.postOfficeWeek);
    const x = z.days[dayOfWeek - 1].times.findIndex((v) => v.id === oldId);
    delete z.days[dayOfWeek - 1].times[x]?.created;
    delete z.days[dayOfWeek - 1].times[x]?.updated;
    delete z.days[dayOfWeek - 1].times[x]?.deleted;
    z.days[dayOfWeek - 1].times[x].id = newId ?? z.days[dayOfWeek - 1].times[x]?.id;
    runInAction(() => {
      schedulerReferenceStore.postOfficeWeek = cloneDeep(z);
    });
  },

  deleteInPostOfficeWeeks: async (dayOfWeek:number, oldId:string): Promise<void> => {
    // функция затирает удалённый элемент полностью после удаления в базе
    const z = cloneDeep(schedulerReferenceStore.postOfficeWeek);
    const x = z.days[dayOfWeek - 1].times.findIndex((v) => v.id === oldId);
    z.days[dayOfWeek - 1].times.splice(x, 1);
    runInAction(() => {
      schedulerReferenceStore.postOfficeWeek = cloneDeep(z);
    });
  },

  createPostOfficeWeek: async (id: string, request: CreatePostOfficeWeekInterface): Promise<void> => {
    schedulerReferenceActions.clearError();
    const response = await schedulerController.createPostOfficeWeek(request);
    schedulerReferenceActions.handleError(response, id);
    runInAction(() => {
      if (!response.errorData) {
        schedulerReferenceActions.updateIdInPostOfficeWeeks(+request.dayOfWeek, id, response?.data?.id);
      }
    });
  },

  updatePostOfficeWeek: async (id: string, dayOfWeek:number, request: UpdatePostOfficeWeekInterface): Promise<void> => {
    schedulerReferenceActions.clearError();
    const response = await schedulerController.updatePostOfficeWeek(id, request);
    schedulerReferenceActions.handleError(response, id);
    runInAction(() => {
      if (!response.errorData) {
        schedulerReferenceActions.updateIdInPostOfficeWeeks(dayOfWeek, id);
      }
    });
  },

  deletePostOfficeWeek: async (id: string, dayOfWeek:number): Promise<void> => {
    schedulerReferenceActions.clearError();
    const response = await schedulerController.deletePostOfficeWeek(id);
    schedulerReferenceActions.handleError(response, id);
    if (!response.errorData) {
      runInAction(() => {
        if (!response.errorData) {
          schedulerReferenceActions.deleteInPostOfficeWeeks(dayOfWeek, id);
        }
      });
    }
  },

};
